/**
 * Service layer — Construction · ASIs (Supabase-backed)
 *
 * Wraps /api/asis (Clerk-authed, service_role). Architect's Supplemental Instructions,
 * a CA sub-module scoped to a construction_contract. Each ASI is linked 1:1 to a Cortex
 * Thread (thread_airtable_id) that owns status / deadline / activity / Slack.
 * Components call only these functions.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/asis' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'ASI request failed: ' + res.status)
  }
  return res.json()
}

export const ASI_STATUS_LABELS = { draft: 'Draft', issued: 'Issued', rescinded: 'Rescinded' }

/* ── Reads ────────────────────────────────────────────────────────────────── */

export function getContractAsis(contractId) {
  if (!contractId) return Promise.resolve([])
  return call('GET', null, 'contractId=' + encodeURIComponent(contractId)).then(d => d.asis || [])
}
export function getAsi(asiId) {
  return call('GET', null, 'asiId=' + encodeURIComponent(asiId)).then(d => d.asi)
}
/** Reverse lookup: the ASI linked to a milestone (milestone -> ASI mount), or null. */
export function getAsiByMilestone(milestoneAirtableId) {
  if (!milestoneAirtableId) return Promise.resolve(null)
  return call('GET', null, 'milestoneId=' + encodeURIComponent(milestoneAirtableId)).then(d => d.asi || null)
}

/** Natural next ASI # in sequence — project-scoped (falls back to contract-scoped). Mirrors
 *  getNextRfiNumber in services/rfis.js. */
export function getNextAsiNumber(projectId, contractId) {
  return call('POST', { action: 'nextAsiNumber', projectId, contractId }).then(d => d.next)
}

/* ── Writes ───────────────────────────────────────────────────────────────── */

export function createAsi(contractId, projectId, fields) {
  return call('POST', { action: 'createAsi', contractId, projectId, fields }).then(d => d.asi)
}
export function updateAsi(asiId, fields) {
  return call('POST', { action: 'updateAsi', asiId, fields }).then(d => d.asi)
}
export function deleteAsi(asiId) {
  return call('POST', { action: 'deleteAsi', asiId }).then(d => d.ok)
}

/** Transition an ASI to issued (draft → issued, or rescinded → reissued). Resets issue_date to today and notifies subscribers. */
export function issueAsi(asiId) {
  return call('POST', { action: 'issueAsi', asiId }).then(d => d.asi)
}

/** Rescind an issued ASI — unlocks edit, hides its content from the GC portal (leaves a ghost record), and notifies subscribers. */
export function rescindAsi(asiId, note) {
  return call('POST', { action: 'rescindAsi', asiId, note: note || null }).then(d => d.asi)
}

/** Regenerate an issued ASI's PDF with no status/date/notification change — backfills ASIs issued before the native exporter existed, or refreshes one after a template fix. */
export function generateAsiPdf(asiId) {
  return call('POST', { action: 'generatePdf', asiId }).then(d => d.asi)
}

/** Repair the ASI's milestone link (and clear any stale thread_airtable_id). Allowed at any status — linkage repair, not content editing. Pass milestoneId=null to unlink. */
export function linkAsiMilestone(asiId, milestoneId) {
  return call('POST', { action: 'linkMilestone', asiId, milestoneId: milestoneId || null }).then(d => d.asi)
}

/**
 * Get a signed upload URL for attaching a document PDF to an ASI.
 * Returns { uploadUrl, publicUrl, path }.
 * Client PUTs the file to uploadUrl, then calls updateAsi(asiId, { document_url: publicUrl }).
 */
export function getAsiUploadUrl(asiId, filename) {
  return call('POST', { action: 'getUploadUrl', asiId, filename }).then(d => d)
}

/**
 * Render a draft-stamped preview of the ASI PDF (current field values + bound attachments, not
 * saved) so it can be reviewed before Issue is clicked. Returns { pdfUrl } (an object URL the
 * caller opens) or { error }.
 */
export async function previewAsiPdf(asiId) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/asis', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'previewAsi', asiId }),
  })
  if (!res.ok) {
    const j = await res.json().catch(() => ({}))
    return { error: j.error || ('Preview failed (' + res.status + ')') }
  }
  const blob = await res.blob()
  return { pdfUrl: URL.createObjectURL(blob) }
}
